import { motion } from 'framer-motion';
import { ArrowRight, Users, Trophy, Calendar, Dumbbell, Sparkles, MapPin, Phone, Mail, Send, Heart, Star, Zap, PartyPopper, Clock, Handshake, Megaphone, Store, Package, PanelTop } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    queryType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dbVenues, setDbVenues] = useState([]);
  const [dbEvents, setDbEvents] = useState([]);

  // Fetch venues and events from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [venuesRes, eventsRes] = await Promise.all([
          axios.get(`${API}/venues`),
          axios.get(`${API}/events`)
        ]);
        if (venuesRes.data.length > 0) {
          setDbVenues(venuesRes.data);
        }
        setDbEvents(eventsRes.data);
      } catch (error) {
        console.log('Using default venues');
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      queryType: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        location: formData.queryType,
        message: formData.message
      });
      toast.success('Thank you! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        queryType: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      title: 'Pay & Play',
      description: 'Book instantly, play whenever',
      icon: Trophy,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Community Games',
      description: 'Find your squad, make friends',
      icon: Users,
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Events & Tournaments',
      description: 'Compete, win, celebrate',
      icon: Calendar,
      gradient: 'from-orange-500 to-red-500'
    },
    {
      title: 'Beginner & Pro Coaching',
      description: 'Level up your game',
      icon: Dumbbell,
      gradient: 'from-emerald-500 to-teal-500'
    }
  ];

  const [selectedCity, setSelectedCity] = useState('All');
  
  // Use only database venues
  const venuesList = dbVenues;
  const venueCities = ['All', ...new Set(venuesList.map(loc => loc.city))].sort();
  const locations = selectedCity === 'All' ? venuesList : venuesList.filter(loc => loc.city === selectedCity);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center bg-gradient-to-br from-blue-50 via-white to-cyan-50 pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1, 1.3, 1], rotate: [0, -90, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-lime-500 to-green-500 text-slate-900 px-5 py-2.5 rounded-full text-sm font-semibold mb-8 shadow-lg shadow-lime-500/30"
              >
                <Sparkles className="w-4 h-4" />
                Community First Sports Brand
              </motion.div>
              
              <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black text-slate-900 mb-6 leading-[0.9]">
                Play.
                <br />
                <span className="bg-gradient-to-r from-lime-500 via-green-500 to-emerald-500 bg-clip-text text-transparent">Connect.</span>
                <br />
                Compete.
              </h1>
              
              <p className="text-xl text-slate-600 mb-10 leading-relaxed max-w-xl">
                Your Club. Your Community. Join 250,000+ active members at India's community-first sports brand.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => document.getElementById('locations').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-lime-500 to-green-500 hover:from-lime-600 hover:to-green-600 text-slate-900 rounded-full px-10 py-7 text-lg font-bold shadow-xl shadow-lime-500/30 hover:shadow-2xl hover:shadow-lime-500/40 transition-all hover:scale-105 group"
                >
                  Book Now
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => document.getElementById('community').scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  className="bg-white/80 backdrop-blur-sm text-slate-900 border-2 border-slate-300 hover:bg-white hover:border-blue-500 hover:text-blue-600 rounded-full px-10 py-7 text-lg font-semibold hover:scale-105 transition-all"
                >
                  Join The CoPlay Community
                </Button>
                <Button
                  onClick={() => document.getElementById('host-event').scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  className="bg-white/80 backdrop-blur-sm text-slate-900 border-2 border-slate-300 hover:bg-white hover:border-purple-500 hover:text-purple-600 rounded-full px-10 py-7 text-lg font-semibold hover:scale-105 transition-all"
                >
                  Host An Event
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative">
                <motion.div whileHover={{ scale: 1.05, rotate: 2 }} className="relative z-10">
                  <img
                    src="https://customer-assets.emergentagent.com/job_003b06d3-59c3-4675-87b7-36b976ad754e/artifacts/r6olontp_Picture%201.png"
                    alt="CoPlay Padel Courts"
                    className="rounded-[2.5rem] shadow-2xl w-full h-[500px] object-cover border-8 border-white"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-600/20 to-transparent rounded-[2.5rem]"></div>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  className="absolute -bottom-8 -right-8 z-20"
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_003b06d3-59c3-4675-87b7-36b976ad754e/artifacts/jenmt4ty_2025-02-24-22-19-57-642.PNG"
                    alt="CoPlay Basketball Courts"
                    className="rounded-3xl shadow-2xl w-64 h-48 object-cover border-8 border-white"
                    data-testid="hero-image-badminton"
                  />
                </motion.div>

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {(() => {
              const stats = [
                { icon: Users, value: '250K+', label: 'Active Members' },
                { icon: Zap, value: '25K+', label: 'Games Hosted' },
                { icon: Heart, value: '5K+', label: 'Community Activations' },
                { icon: Star, value: '500+', label: 'Events & Engagements' }
              ];
              
              return stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-lime-500 to-green-500 mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-slate-900" />
                    </div>
                    <div className="text-3xl font-black bg-gradient-to-r from-lime-500 to-green-500 bg-clip-text text-transparent mb-2">{stat.value}</div>
                    <div className="text-slate-600 font-semibold">{stat.label}</div>
                  </motion.div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* Luxury Meets Performance */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='80' height='80' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='1' cy='1' r='1' fill='white' fill-opacity='0.05'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")`}}></div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-block bg-gradient-to-r from-lime-500 to-green-500 text-slate-900 px-4 py-2 rounded-full text-sm font-bold mb-6">
              ⭐ Your New Hangout Spot
            </div>
            <h2 className="text-5xl sm:text-6xl font-black mb-6">
              More Than Just Sports
              <br />
              <span className="bg-gradient-to-r from-lime-400 to-green-400 bg-clip-text text-transparent">It's Your Community</span>
            </h2>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed">
              A safe, social space where you can chill with family, make new friends, and be part of something bigger. CoPlay is where connections happen.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {[
                { title: 'Top Sports & Thoughtfully Designed Facilities', desc: 'Pickleball, padel, badminton, football, cricket & more — great lighting, great surfaces, great energy' },
                { title: 'A Safe, Inclusive Space for Everyone', desc: 'Beginners, regulars, mixed groups, solo players — everyone belongs here. Zero intimidation, all good vibes' },
                { title: 'More Than Sports — It\'s a Social Scene', desc: 'Come for the game, stay for the conversations. Coffee, post-match chats, casual meet-ups' },
                { title: 'Easy, Flexible & Hassle-Free', desc: 'Book instantly, show up, play - just turn up when you feel like it' },
                { title: 'Community-Led, Always Happening', desc: 'Mixers, leagues, social games, watch parties — there\'s always something going on' },
                { title: 'Your Everyday Escape from the Routine', desc: 'A place to unwind, move, laugh and recharge — before work, after work, or all weekend long' }
              ].map((amenity, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col gap-3 bg-white/10 backdrop-blur-sm rounded-2xl p-5 hover:bg-white/20 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-lime-500 to-green-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-5 h-5 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-base font-bold text-left leading-tight">{amenity.title}</h3>
                  </div>
                  <p className="text-sm text-slate-300 text-left leading-relaxed pl-11">{amenity.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Makes Us Awesome */}
      <section id="services" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl sm:text-6xl font-black text-slate-900 mb-6">
              What Makes Us
              <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent"> Awesome</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Everything you need for the ultimate sports experience
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="relative group"
                >
                  <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border-2 border-slate-100 hover:border-transparent overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 transition-opacity`}></div>
                    
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <h3 className="text-3xl font-bold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 text-lg">{service.description}</p>
                    
                    <div className={`absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-br ${service.gradient} opacity-10 rounded-tl-full`}></div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Locations - Ready to Play */}

      {/* CoPlay Communities */}
      <section id="community" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl sm:text-6xl font-black text-slate-900 mb-6">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">CoPlay</span> Communities
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Join our vibrant community of sports enthusiasts. Play together, compete together, grow together.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl p-8 text-white shadow-xl"
            >
              <Users className="w-16 h-16 mb-6" />
              <h3 className="text-3xl font-black mb-4">Community Games</h3>
              <p className="text-lg text-white/90 mb-6">
                Join weekly community matches, meet fellow players, and make lasting friendships. Open to all skill levels!
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">✓</div>
                  <span>Weekly matches & tournaments</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">✓</div>
                  <span>Skill-based team matching</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">✓</div>
                  <span>Social events & meetups</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-500 to-red-500 rounded-3xl p-8 text-white shadow-xl"
            >
              <Calendar className="w-16 h-16 mb-6" />
              <h3 className="text-3xl font-black mb-4">Regular Events</h3>
              <p className="text-lg text-white/90 mb-6">
                Participate in exciting tournaments, themed sports days, and special competitions throughout the year.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">✓</div>
                  <span>Monthly tournaments</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">✓</div>
                  <span>Festival special events</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">✓</div>
                  <span>Prizes & recognition</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8 text-center"
          >
            <h3 className="text-3xl font-black text-slate-900 mb-4">Join Your Regional Community</h3>
            <p className="text-lg text-slate-600 mb-8">
              Connect with players in your area through our region-wise community groups
            </p>
            <Button
              asChild
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-full px-10 py-7 text-lg font-bold shadow-xl hover:scale-105 transition-all"
            >
              <a href="https://linktr.ee/coplaycommunity" target="_blank" rel="noopener noreferrer">
                Join Community Groups 🎯
              </a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Host an Event */}
      <section id="host-event" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='2' cy='2' r='2' fill='white' fill-opacity='0.1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")`}}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <PartyPopper className="w-20 h-20 mb-6 text-yellow-400" />
              <h2 className="text-5xl sm:text-6xl font-black mb-6">
                Host an Event
              </h2>
              <p className="text-xl text-white/80 mb-10">
                Get in touch with us today and let's create an unforgettable experience for you and your guests!
              </p>
              <Button
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-slate-900 rounded-full px-10 py-7 text-lg font-black shadow-xl hover:scale-105 transition-all"
              >
                Contact Us for Events 🎊
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {[
                { icon: '🎂', title: 'Birthday Parties', desc: 'Celebrate with sports & fun' },
                { icon: '🏢', title: 'Corporate Events', desc: 'Team building activities' },
                { icon: '🎓', title: 'School Events', desc: 'Educational sports programs' },
                { icon: '👨‍👩‍👧‍👦', title: 'Family Gatherings', desc: 'Create lasting memories' }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-5xl">{item.icon}</span>
                    <div>
                      <h4 className="text-2xl font-bold">{item.title}</h4>
                      <p className="text-white/80">{item.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brand Partnerships */}
      <section id="partnerships" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Handshake className="w-20 h-20 mx-auto mb-6 text-lime-500" />
            <h2 className="text-5xl sm:text-6xl font-black text-slate-900 mb-6">
              Partner With <span className="bg-gradient-to-r from-lime-500 via-green-500 to-emerald-500 bg-clip-text text-transparent">CoPlay</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Help your brand reach the right audience at India's premium sports venues
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
            {[
              { icon: PanelTop, title: 'Venue Branding', desc: 'Premium visibility at high-footfall locations', color: 'text-blue-500' },
              { icon: Megaphone, title: 'Event Sponsorship', desc: 'Associate your brand with community events', color: 'text-purple-500' },
              { icon: Store, title: 'Pop-up Kiosks', desc: 'Set up shop and engage directly with our active community', color: 'text-orange-500' },
              { icon: Package, title: 'Product Sampling', desc: 'Get your products in the hands of fitness enthusiasts', color: 'text-pink-500' }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-4 md:p-6 hover:shadow-lg transition-all text-center border border-slate-200"
              >
                <IconComponent className={`w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 md:mb-4 ${item.color}`} />
                <h3 className="text-base md:text-xl font-bold text-slate-900 mb-1 md:mb-2">{item.title}</h3>
                <p className="text-slate-600 text-xs md:text-sm">{item.desc}</p>
              </motion.div>
            );})}
          </div>

          {/* Brand Partnership Images */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
            {[
              { src: 'https://customer-assets.emergentagent.com/job_003b06d3-59c3-4675-87b7-36b976ad754e/artifacts/pjnmk68x_100A1501.jpg', alt: 'Brand partnership at CoPlay' },
              { src: 'https://customer-assets.emergentagent.com/job_003b06d3-59c3-4675-87b7-36b976ad754e/artifacts/dw8yrkjz_Copy%20of%20IMG20251219201859.jpg', alt: 'Event sponsorship' },
              { src: 'https://customer-assets.emergentagent.com/job_003b06d3-59c3-4675-87b7-36b976ad754e/artifacts/gb0w6i13_Copy%20of%2020250726_153610.jpg', alt: 'Pop-up kiosk' },
              { src: 'https://customer-assets.emergentagent.com/job_003b06d3-59c3-4675-87b7-36b976ad754e/artifacts/vtjjetlc_Copy%20of%20IMG_7532.JPG', alt: 'Product sampling' },
              { src: 'https://customer-assets.emergentagent.com/job_003b06d3-59c3-4675-87b7-36b976ad754e/artifacts/ey8aszgv_20250726_175941.jpg', alt: 'Brand activation' }
            ].map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="relative overflow-hidden rounded-2xl aspect-square shadow-lg"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Partner with us to connect with our thriving community of sports enthusiasts across India.
            </p>
            <Button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-lime-500 via-green-500 to-emerald-500 hover:from-lime-600 hover:via-green-600 hover:to-emerald-600 text-white rounded-full px-10 py-7 text-lg font-black shadow-xl hover:scale-105 transition-all"
            >
              Get In Touch 🤝
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CoPlay Communities */}
      <section id="locations" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='2' cy='2' r='2' fill='white' fill-opacity='0.1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")`}}></div>
        
        <div className="max-w-7xl mx-auto relative z-10 text-white mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              className="inline-block text-7xl mb-6"
            >
              🎉
            </motion.div>
            <h2 className="text-5xl sm:text-6xl font-black mb-6">
              Ready to Play?
            </h2>
            <p className="text-2xl text-white/90 mb-10 font-medium">
              Find your nearest CoPlay location and book your slot now!
            </p>
          </motion.div>

          <div className="flex justify-center px-4">
            <div className="flex flex-wrap items-center justify-center gap-2 bg-white/10 backdrop-blur-md rounded-3xl p-2 max-w-5xl">
              {venueCities.map((city) => (
                <button
                  key={city}
                  onClick={() => setSelectedCity(city)}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all text-sm sm:text-base ${
                    selectedCity === city
                      ? 'bg-white text-blue-600 shadow-lg'
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  {city}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {locations.map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                whileHover={{ y: -12 }}
                className="group"
              >
                <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                  <div className="relative h-48 overflow-hidden">
                    <motion.img
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.4 }}
                      src={location.image}
                      alt={location.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-black text-slate-900 mb-2">{location.name}</h3>
                    <p className="text-base text-slate-600 mb-4 font-semibold">{location.city}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {location.sports.split(', ').map((sport, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold"
                        >
                          {sport}
                        </span>
                      ))}
                    </div>
                    
                    <Button
                      asChild
                      className={`w-full bg-gradient-to-r ${location.gradient} hover:opacity-90 text-white rounded-full font-bold py-6 shadow-lg hover:shadow-xl hover:scale-105 transition-all group`}
                    >
                      <a href={location.link} target="_blank" rel="noopener noreferrer">
                        Book Now <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </a>
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Events & Community Games */}
      {dbEvents.length > 0 && (
        <section id="events" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='2' cy='2' r='2' fill='white' fill-opacity='0.1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")`}}></div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16 text-white"
            >
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                className="inline-block text-7xl mb-6"
              >
                🎯
              </motion.div>
              <h2 className="text-5xl sm:text-6xl font-black mb-6">
                Events & Community Games
              </h2>
              <p className="text-2xl text-white/90 font-medium max-w-3xl mx-auto">
                Join exciting tournaments, community games, and special events. Click to register!
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {dbEvents.map((event, index) => (
                <motion.div
                  key={event.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -12 }}
                  className="group"
                >
                  <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                    <div className="relative h-48 overflow-hidden">
                      <motion.img
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.4 }}
                        src={event.image}
                        alt={event.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-2 text-white/90 text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>{event.date}</span>
                          <span className="mx-1">•</span>
                          <Clock className="w-4 h-4" />
                          <span>{event.time}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-black text-slate-900 mb-2">{event.name}</h3>
                      <p className="text-base text-slate-600 mb-4 font-semibold flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> {event.venue}
                      </p>
                      
                      <Button
                        asChild
                        className={`w-full bg-gradient-to-r ${event.gradient || 'from-orange-500 to-red-500'} hover:opacity-90 text-white rounded-full font-bold py-6 shadow-lg hover:shadow-xl hover:scale-105 transition-all group`}
                      >
                        <a href={event.link} target="_blank" rel="noopener noreferrer">
                          Register Now <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Form */}
      <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-5xl font-black text-slate-900 mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-slate-600">
              Have questions? We're here to help!
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 shadow-xl"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="rounded-xl border-slate-200 bg-slate-50 focus:ring-blue-600 focus:border-blue-600"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="rounded-xl border-slate-200 bg-slate-50 focus:ring-blue-600 focus:border-blue-600"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="rounded-xl border-slate-200 bg-slate-50 focus:ring-blue-600 focus:border-blue-600"
                  />
                </div>

                <div>
                  <Label htmlFor="queryType" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Type of Query *
                  </Label>
                  <Select value={formData.queryType} onValueChange={handleSelectChange} required>
                    <SelectTrigger className="rounded-xl border-slate-200 bg-slate-50 focus:ring-blue-600 focus:border-blue-600">
                      <SelectValue placeholder="Select query type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pay-and-play">Pay & Play</SelectItem>
                      <SelectItem value="membership">Membership</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="coaching">Coaching</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Your Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us how we can help you..."
                  rows={5}
                  className="rounded-xl border-slate-200 bg-slate-50 focus:ring-blue-600 focus:border-blue-600 resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-full py-7 text-lg font-bold shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100"
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    Send Message <Send className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
